// GrabCAD module — data export builder, window-message TX, file save, and
// file import RX.
//
// TX (importAssembly) and saveFile are request/response: each outgoing message
// carries an id and the function awaits the matching response. The file
// import RX flow has no correlation id — its messages are discriminated by
// their `info` field, so only one import may be in flight at a time.

import type { GrabCADExport, GCMeshPart, GCFileDep, ExportOptions } from './GrabCADTypes.ts';
import { isMeshPart } from '../_utils.ts';
import type { FileData, FileExportArray, FileIdx, ValidFilename } from '../../../../data/data-interfaces.ts';
import { devlog } from '../../../../util/utils.ts';
import { isProduction } from '../../../../data/constants.ts';
import { unzipSync, strFromU8 } from 'fflate';

// ============================================================================
// Validation
// ============================================================================

export interface ValidationError {
  fileKey: string;
  fileName?: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

export function validateForGrabCAD(files: FileExportArray): ValidationResult {
  const errors: ValidationError[] = [];
  for (const file of files) {
    if (!isMeshPart(file)) continue;
    if (file.fileType !== '3mf') {
      errors.push({
        fileKey: file.fileKey,
        fileName: file.fileName,
        message: `fileType is "${file.fileType}" — GrabCAD requires "3mf"`,
      });
    }
    if (file.data.byteLength === 0) {
      errors.push({
        fileKey: file.fileKey,
        fileName: file.fileName,
        message: 'data is empty',
      });
    }
  }
  return { valid: errors.length === 0, errors };
}

// ============================================================================
// buildGrabCADExport — pure transformer from populated FileExportArray
// ============================================================================

function defaultURN(file: FileData): string {
  return `${file.appSource}:${file.category}:${file.fileKey}`;
}

export async function buildGrabCADExport(
  files: FileExportArray,
  opts: ExportOptions = {}
): Promise<GrabCADExport> {
  const exported: GrabCADExport = {
    name: opts.packageName ?? '',
    parts: [],
    dependencies: []
  };

  const getURN = opts.getURN ?? defaultURN;
  const partMap = new Map<FileIdx, GCMeshPart>();

  for (const file of files) {
    if (!isMeshPart(file)) continue;

    const data = file.data;

    const part: GCMeshPart = {
      name: (file.fileName || file.fileKey) as ValidFilename,
      data,
      extension: '3mf',
      suppress: file.category === 'workpiece',
      URN: getURN(file),
      dependencies: []
    };

    exported.parts.push(part);
    partMap.set(file.fileIdx, part);
  }

  for (const file of files) {
    if (isMeshPart(file)) continue;

    const dep: GCFileDep = {
      name: (file.fileName || file.fileKey) as ValidFilename,
      extension: file.fileType,
      data: file.data,
      URN: getURN(file)
    };

    const parentPart = file.parent !== undefined ? partMap.get(file.parent) : null;
    if (parentPart) {
      parentPart.dependencies.push(dep);
    } else {
      exported.dependencies.push(dep);
    }

  }

  return exported;
}

// ============================================================================
// Shared messaging infrastructure
// ============================================================================

export interface GrabCadOptions {
  /** Window to send messages to (default: window.opener). */
  target?: Window;
  /** postMessage targetOrigin (default: '*'). */
  targetOrigin?: string;
  /**
   * EventTarget to listen on for response messages (default: window).
   * Override in tests or non-window contexts.
   */
  listenOn?: EventTarget;
  /**
   * Called immediately before the assembly is posted — the last instant at
   * which nothing has left the app. Throw from here to abandon the send; the
   * error surfaces as the rejection of the grabCadTX call. Used to make the
   * cancel check as late as it can possibly be: everything grabCadTX does
   * before this point (building the transfer list, the dev diagnostic that
   * unzips each 3MF) is still work that can be thrown away.
   */
  onBeforeSend?: () => void;
}

function resolveOpts(opts: GrabCadOptions) {
  return {
    target: opts.target ?? window.opener as Window,
    origin: opts.targetOrigin ?? '*',
    listenOn: opts.listenOn ?? window as EventTarget,
  };
}

// ============================================================================
// TX — transmit a GrabCADExport package to GrabCAD
// ============================================================================

/**
 * Dev diagnostic only: count geometry inside a 3MF buffer (a zip whose mesh lives
 * in a `*.model` XML entry). Returns null if the buffer isn't a parseable 3MF.
 * triangles=0 means an empty-geometry mesh — the kind GrabCAD's importer chokes on.
 */
function count3mfGeometry(data: ArrayBuffer): { objects: number; triangles: number; vertices: number } | null {
  try {
    const files = unzipSync(new Uint8Array(data));
    // 3MF geometry may be split across several *.model parts (production extension),
    // so sum over all of them rather than trusting the first.
    const modelPaths = Object.keys(files).filter((p) => p.toLowerCase().endsWith('.model'));
    if (modelPaths.length === 0) return null;
    const totals = { objects: 0, triangles: 0, vertices: 0 };
    for (const path of modelPaths) {
      const xml = strFromU8(files[path]);
      totals.objects   += (xml.match(/<object\b/g)   || []).length;
      totals.triangles += (xml.match(/<triangle\b/g) || []).length;
      totals.vertices  += (xml.match(/<vertex\b/g)   || []).length;
    }
    return totals;
  } catch {
    return null;
  }
}

/**
 * Transmits a GrabCADExport to GrabCAD in a single postMessage call.
 * ArrayBuffers are transferred — the pkg buffers are neutered after this call.
 * Returns the generated assembly ID.
 *
 *   → {command: 'importAssembly', id, data: GrabCADExport}
 *   ← {info: 'assemblyFinalized', id}  — success
 *   ← {info: 'assemblyError', error}   — failure
 */
export async function grabCadTX(pkg: GrabCADExport, opts: GrabCadOptions = {}): Promise<string> {
  const { target, origin, listenOn } = resolveOpts(opts);
  const id = crypto.randomUUID();

  const transfers: ArrayBuffer[] = [];
  for (const part of pkg.parts) {
    if (part.data.byteLength > 0) transfers.push(part.data);
    for (const dep of part.dependencies) {
      if (dep.data.byteLength > 0) transfers.push(dep.data);
    }
  }
  for (const dep of pkg.dependencies) {
    if (dep.data.byteLength > 0) transfers.push(dep.data);
  }

  // Non-production diagnostic: log the assembly contents before the buffers are
  // transferred (and thus neutered) by postMessage. A part with a near-empty
  // byteLength is a degenerate/empty mesh that GrabCAD's importer can choke on.
  if (!isProduction) {
    console.log(
      `[grabCadTX] importAssembly "${pkg.name}" — ${pkg.parts.length} part(s), ` +
      `${pkg.dependencies.length} top-level dep(s), id=${id}`
    );
    const geomCols = (ext: string, data: ArrayBuffer) => {
      const g = ext === '3mf' ? count3mfGeometry(data) : null;
      return { triangles: g ? g.triangles : '', objects: g ? g.objects : '' };
    };
    console.table([
      ...pkg.parts.map((p) => ({
        kind: 'part', name: p.name, ext: p.extension, bytes: p.data.byteLength,
        ...geomCols(p.extension, p.data),
        suppress: p.suppress, deps: p.dependencies.length, URN: p.URN,
      })),
      ...pkg.parts.flatMap((p) => p.dependencies.map((d) => ({
        kind: 'part-dep', name: d.name, ext: d.extension, bytes: d.data.byteLength,
        ...geomCols(d.extension, d.data),
        suppress: '', deps: '', URN: d.URN,
      }))),
      ...pkg.dependencies.map((d) => ({
        kind: 'dep', name: d.name, ext: d.extension, bytes: d.data.byteLength,
        ...geomCols(d.extension, d.data),
        suppress: '', deps: '', URN: d.URN,
      })),
    ]);
  }

  await new Promise<void>((resolve, reject) => {
    function onMessage(event: MessageEvent): void {
      const d = event.data;
      if (d?.info === 'assemblyFinalized' && d?.id === id) {
        listenOn.removeEventListener('message', onMessage);
        resolve();
      } else if (d?.info === 'assemblyError') {
        listenOn.removeEventListener('message', onMessage);
        reject(new Error(d?.error ?? 'GrabCAD assembly error'));
      }
    }
    listenOn.addEventListener('message', onMessage);
    try {
      // Nothing has left the app until the line below. onBeforeSend is the last
      // chance to abandon the send — GrabCAD has no message that recalls an
      // assembly once it is posted (abortConvert is the RX flow only).
      opts.onBeforeSend?.();
      target.postMessage({ command: 'importAssembly', id, data: pkg }, origin, transfers);
    } catch (err) {
      listenOn.removeEventListener('message', onMessage);
      reject(err);
    }
  });

  return id;
}

// ============================================================================
// Save file — trigger GrabCAD/Electron's native save dialog
// ============================================================================

/** Result of a grabCadSaveFile call. */
export interface GrabCadSaveResult {
  /** False when the user cancelled the save dialog. */
  success: boolean;
  /** Path the file was written to (present only on success). */
  filepath?: string;
}

export interface GrabCadSaveFileOptions extends GrabCadOptions {
  /** Native dialog title (default 'Save File'). */
  title?: string;
  /** Extension shown in the dialog's file filter, without the dot
   *  (default: the filename's extension). */
  fileExtension?: string;
  /** Human-readable name for the extension filter (default '<EXT> File'). */
  fileExtensionName?: string;
}

/** Longest we'll wait for a saveFileResult before giving up. A real native
 *  dialog can sit open for a while on a slow user, so this is generous — it
 *  only exists to catch a host that drops the message entirely (e.g. right
 *  after a transfer shifts its UI focus away from this webview), which
 *  otherwise leaves the caller's promise — and any UI gated on it — hung
 *  forever with no error and no way to recover. */
const SAVE_FILE_TIMEOUT_MS = 120_000;

/**
 * Asks GrabCAD to show a native save dialog and write the given blob to disk.
 * Resolves once GrabCAD reports the result — await this before closing the
 * window. The blob's ArrayBuffer is transferred with the request. If GrabCAD
 * never responds (see SAVE_FILE_TIMEOUT_MS), resolves as if cancelled rather
 * than hanging indefinitely.
 *
 * Messages:
 *   → {command: 'saveFile', id, data: {filename, data, title, fileExtension, fileExtensionName}}
 *   ← {info: 'saveFileResult', id, success: true, filepath}   — saved
 *   ← {info: 'saveFileResult', id, success: false}            — user cancelled
 */
export async function grabCadSaveFile(
  blob: Blob,
  filename: string,
  opts: GrabCadSaveFileOptions = {}
): Promise<GrabCadSaveResult> {
  const { target, origin, listenOn } = resolveOpts(opts);
  const id = crypto.randomUUID();
  const data = await blob.arrayBuffer();
  const fileExtension = opts.fileExtension ?? filename.split('.').pop() ?? '';

  return new Promise<GrabCadSaveResult>((resolve) => {
    const timeoutTimer = setTimeout(() => {
      listenOn.removeEventListener('message', onMessage);
      console.error('[GrabCAD saveFile] No saveFileResult received within timeout, id:', id);
      resolve({ success: false });
    }, SAVE_FILE_TIMEOUT_MS);

    function onMessage(event: MessageEvent): void {
      const d = event.data;
      if (d?.info !== 'saveFileResult') return;
      devlog('[GrabCAD saveFile] saveFileResult received:', { receivedId: d?.id, expectedId: id, success: d?.success, filepath: d?.filepath });
      // Accept matching id, or an id-less response (GrabCAD may omit the id on cancel)
      if (d?.id !== undefined && d?.id !== id) return;
      clearTimeout(timeoutTimer);
      listenOn.removeEventListener('message', onMessage);
      resolve(d.success ? { success: true, filepath: String(d.filepath) } : { success: false });
    }
    listenOn.addEventListener('message', onMessage);
    devlog('[GrabCAD saveFile] sending saveFile, id:', id, 'filename:', filename);
    target.postMessage({
      command: 'saveFile',
      id,
      data: {
        filename,
        data,
        title: opts.title ?? 'Save File',
        fileExtension,
        fileExtensionName: opts.fileExtensionName ?? `${fileExtension.toUpperCase()} File`,
      },
    }, origin);
  });
}

// ============================================================================
// RX — import a file from GrabCAD
// ============================================================================
//
// Protocol (responses are discriminated by their `info` field; the newer host
// adds an `id` to progress/error/abort messages, but openFileData still has
// none — so only one import flow may be in flight at a time):
//
//   → {command: 'openFile', multifile}
//   ← {info: 'openFileResult', files: [<string>]}             (empty when dismissed)
//       (legacy hosts: {info: 'openFileResult', filepath} — still accepted)
//
//   → {command: 'convertAndOpenFile', filepath, ...settings}  (settings.unit for STL,
//       plus optional repair / tessellationQuality / targetTriangleCount / detectClashes)
//   ← {info: 'openFileProgress', progress}                    (repeated; newer hosts send
//       stage strings 'converting' / 'reading', legacy hosts a number)
//   ← {info: 'openFileData', filename, filetype, data}        (success)
//   ← {info: 'openFileError', id?, error}                     (failure)
//   ← {info: 'openFileAborted', id?}                          (after abortConvert)
//
//   → {command: 'abortConvert', id?}

export type GrabCadStlUnit = 'mm' | 'cm' | 'in' | 'm' | 'ft';

/** Conversion progress: newer hosts report stage strings ('converting',
 *  'reading'), legacy hosts a number. */
export type GrabCadImportProgress = string | number;

/** Conversion settings sent alongside convertAndOpenFile. */
export interface GrabCadConvertSettings {
  /** Interpretation unit for unitless formats (currently only STL). */
  unit?: GrabCadStlUnit;
  repair?: boolean;
  tessellationQuality?: 'lowQuality' | 'highQuality';
  targetTriangleCount?: number;
  detectClashes?: boolean;
}

/** A file opened and converted by GrabCAD, ready for app consumption. */
export interface GrabCadImportedFile {
  filename: string;
  filetype: string;
  /** Converted file data. */
  data: ArrayBuffer;
}

export interface GrabCadImportOptions extends GrabCadOptions {
  /** Unit applied when the chosen file is an STL (default 'mm'). */
  unit?: GrabCadStlUnit;
  /** Allow selecting several files in the open dialog (default false). */
  multifile?: boolean;
  /** Extra conversion settings forwarded to every convertAndOpenFile call
   *  (unit is handled separately, per file). */
  convertSettings?: Omit<GrabCadConvertSettings, 'unit'>;
  /** Called for each openFileProgress message during conversion. */
  onProgress?: (progress: GrabCadImportProgress) => void;
  /** Called when the conversion work starts and after each file completes:
   *  `done` files of `total`. The first call (done=0) comes once the open
   *  dialog has closed, which is the first moment the user is waiting on us. */
  onImportProgress?: (done: number, total: number) => void;
  /** Aborts the batch. Every conversion after the current one is skipped. */
  signal?: AbortSignal;
}

// ---- Abort / staleness ----
//
// Only one conversion can be in flight (no correlation id on the RX flow), so a
// single module-level generation is enough to tell a live conversion from one we
// stopped waiting on. Bumped whenever we abandon a conversion.
let convertGeneration = 0;

/** How long we wait for GrabCAD to confirm an abortConvert with openFileAborted
 *  before giving up on it and abandoning the conversion client-side. */
const ABORT_CONFIRM_MS = 4000;

/** How long we keep listening for replies to an abandoned conversion, so a late
 *  one gets reported rather than silently confusing the next import. */
const STALE_WATCH_MS = 60000;

/**
 * Listen for replies belonging to a conversion we already gave up on, and warn
 * once if any arrives. Nothing consumes them — the point is to make a silent
 * protocol problem visible to whoever is debugging imports.
 */
function watchForStaleConvertReplies(listenOn: EventTarget, gen: number): void {
  let timer: ReturnType<typeof setTimeout>;
  function onStale(event: Event): void {
    const info = (event as MessageEvent).data?.info;
    if (info !== 'openFileData' && info !== 'openFileError' &&
        info !== 'openFileProgress' && info !== 'openFileAborted') return;
    stop();
    console.warn(
      `[GrabCAD import] "${info}" arrived for an abandoned conversion ` +
      `(generation ${gen}, now ${convertGeneration}) and was ignored. GrabCAD was ` +
      `asked to abort but did not confirm in time, so it may still have been working. ` +
      `The import RX flow carries no correlation id, so a late reply cannot be matched ` +
      `to its request — this generation guard is what stops it being read as the result ` +
      `of the NEXT import. If GrabCAD ever adds an id to convertAndOpenFile, replace the ` +
      `guard with real correlation and delete this warning.`
    );
  }
  function stop(): void {
    clearTimeout(timer);
    listenOn.removeEventListener('message', onStale);
  }
  listenOn.addEventListener('message', onStale);
  timer = setTimeout(stop, STALE_WATCH_MS);
}

/**
 * Asks GrabCAD to show its native open-file dialog.
 * Resolves with the chosen filepaths (a single entry unless opts.multifile),
 * or null if the dialog was dismissed. Parses the current protocol's
 * `files: [<string>]` result and falls back to the legacy `filepath` field
 * for older hosts. Pass each filepath on to grabCadConvertAndOpenFile to
 * receive the converted data.
 */
export async function grabCadOpenFile(opts: GrabCadOptions & { multifile?: boolean } = {}): Promise<string[] | null> {
  const { target, origin, listenOn } = resolveOpts(opts);

  return new Promise<string[] | null>((resolve) => {
    function onMessage(event: MessageEvent): void {
      const d = event.data;
      if (d?.info !== 'openFileResult') return;
      listenOn.removeEventListener('message', onMessage);
      const files: string[] = Array.isArray(d.files)
        ? d.files.filter((f: unknown): f is string => typeof f === 'string' && f.length > 0)
        : d.filepath ? [String(d.filepath)] : [];
      resolve(files.length ? files : null);
    }
    listenOn.addEventListener('message', onMessage);
    target.postMessage({ command: 'openFile', multifile: !!opts.multifile }, origin);
  });
}

/**
 * Asks GrabCAD to load and convert the file previously chosen via
 * grabCadOpenFile. Progress messages are forwarded to opts.onProgress.
 * Resolves with the converted file, or null if the conversion was aborted
 * (via grabCadAbortConvert). Rejects on openFileError.
 */
export async function grabCadConvertAndOpenFile(
  filepath: string,
  settings: GrabCadConvertSettings = {},
  opts: GrabCadOptions & {
    onProgress?: (progress: GrabCadImportProgress) => void;
    signal?: AbortSignal;
  } = {}
): Promise<GrabCadImportedFile | null> {
  const { target, origin, listenOn } = resolveOpts(opts);
  const gen = ++convertGeneration;

  return new Promise<GrabCadImportedFile | null>((resolve, reject) => {
    // The only id GrabCAD ever hands us for a conversion rides on an
    // openFileProgress message — and only some of them carry it. We echo
    // whatever we saw back on abortConvert; see the abort() comment.
    let hostId: string | undefined;
    let abortTimer: ReturnType<typeof setTimeout> | null = null;

    function finish(settle: () => void): void {
      if (abortTimer !== null) clearTimeout(abortTimer);
      opts.signal?.removeEventListener('abort', abort);
      listenOn.removeEventListener('message', onMessage);
      settle();
    }

    /**
     * Ask GrabCAD to stop, then hold briefly for its openFileAborted.
     *
     * The protocol documents abortConvert as { command, id }, but nothing in the
     * File Opening flow gives us an id to use: convertAndOpenFile carries none,
     * and only the 'reading' openFileProgress reply is documented with one. So
     * the id goes on when we happen to have it and is omitted otherwise, and we
     * do NOT trust the abort to land — if no confirmation arrives we abandon the
     * conversion locally so the user is never stuck waiting on a host that
     * ignored us.
     */
    function abort(): void {
      target.postMessage({ command: 'abortConvert', ...(hostId ? { id: hostId } : {}) }, origin);
      abortTimer = setTimeout(() => {
        console.warn(
          `[GrabCAD import] abortConvert was not confirmed within ${ABORT_CONFIRM_MS}ms ` +
          `(no openFileAborted). The import was abandoned client-side, but GrabCAD may still ` +
          `be converting "${filepath}". We sent ` +
          (hostId ? `id "${hostId}", taken from an openFileProgress reply.` :
                    `no id, because no openFileProgress reply had carried one yet.`) +
          ` The protocol documents abortConvert as { command, id } while convertAndOpenFile ` +
          `has no id to correlate against — if aborts prove unreliable in GrabCAD Print, that ` +
          `mismatch is the first thing to check with Stratasys.`
        );
        watchForStaleConvertReplies(listenOn, gen);
        finish(() => resolve(null));
      }, ABORT_CONFIRM_MS);
    }

    function onMessage(event: MessageEvent): void {
      const d = event.data;
      switch (d?.info) {
        case 'openFileProgress':
          if (typeof d.id === 'string' && d.id) hostId = d.id;
          opts.onProgress?.(d.progress);
          break;
        case 'openFileData':
          finish(() => resolve({ filename: d.filename, filetype: d.filetype, data: d.data }));
          break;
        case 'openFileError':
          finish(() => reject(new Error(d.error ?? 'GrabCAD file conversion error')));
          break;
        case 'openFileAborted':
          finish(() => resolve(null));
          break;
      }
    }

    if (opts.signal?.aborted) {
      // Already cancelled before we got here — don't start work we'd only stop.
      resolve(null);
      return;
    }
    opts.signal?.addEventListener('abort', abort, { once: true });
    listenOn.addEventListener('message', onMessage);
    target.postMessage({ command: 'convertAndOpenFile', filepath, ...settings }, origin);
  });
}

/**
 * Asks GrabCAD to abort an in-flight conversion. GrabCAD answers with
 * openFileAborted, which resolves the pending grabCadConvertAndOpenFile to null.
 *
 * Standalone, so it has no way to know the id of the conversion it is stopping
 * and sends none. Prefer passing a `signal` to grabCadConvertAndOpenFile /
 * grabCadConvertFiles: that path echoes any id GrabCAD gave us and falls back to
 * abandoning the conversion locally when the host never confirms.
 */
export function grabCadAbortConvert(opts: GrabCadOptions = {}): void {
  const { target, origin } = resolveOpts(opts);
  target.postMessage({ command: 'abortConvert' }, origin);
}

// ============================================================================
// User state — small key/value preferences persisted on GrabCAD's side
// ============================================================================
//
// GrabCAD's embedded webview keeps neither localStorage nor IndexedDB across
// separate launches of the app, so anything that needs to survive a relaunch
// (e.g. a "don't show again" flag) has to be stored on GrabCAD's side. These are
// the protocol binding for that; grabcadPlugin.ts wraps them as the prefs
// backend for the 'grabcad' env, which is what app code reaches through
// as-app's setPersistentState/getPersistentState.

/** Longest we'll wait for a userState response. Older GrabCAD hosts may not
 *  support these commands at all, which would otherwise hang the caller's
 *  promise forever. */
const USER_STATE_TIMEOUT_MS = 5_000;

/**
 * A timeout here means the host never answered — almost always a GrabCAD build
 * that doesn't implement these commands. The caller can't tell that apart from
 * "never set", so say it out loud: silently, a preference just never sticks and
 * there's nothing in the console to explain why. Warns rather than devlogs for
 * that reason.
 */
function warnUserStateUnanswered(command: string, state: string): void {
  console.warn(
    `[GrabCAD userState] no answer to ${command} for "${state}" within ${USER_STATE_TIMEOUT_MS}ms — ` +
    `the host may not support user state. Preferences using it will not persist.`
  );
}

/**
 * Asks GrabCAD to persist a small key/value preference.
 *
 *   → {command: 'setUserState', state, value}
 *   ← {info: 'userState', state, value}
 */
export async function grabCadSetUserState(
  state: string,
  value: string,
  opts: GrabCadOptions = {}
): Promise<void> {
  const { target, origin, listenOn } = resolveOpts(opts);

  return new Promise<void>((resolve) => {
    const timeoutTimer = setTimeout(() => {
      listenOn.removeEventListener('message', onMessage);
      warnUserStateUnanswered('setUserState', state);
      resolve();
    }, USER_STATE_TIMEOUT_MS);

    function onMessage(event: MessageEvent): void {
      const d = event.data;
      if (d?.info !== 'userState' || d?.state !== state) return;
      clearTimeout(timeoutTimer);
      listenOn.removeEventListener('message', onMessage);
      devlog('[GrabCAD userState] set acknowledged:', state);
      resolve();
    }
    listenOn.addEventListener('message', onMessage);
    devlog('[GrabCAD userState] sending setUserState:', state, value);
    target.postMessage({ command: 'setUserState', state, value }, origin);
  });
}

/**
 * Asks GrabCAD for a previously-set user state value. Resolves null if it was
 * never set (or if the host doesn't respond in time — see
 * USER_STATE_TIMEOUT_MS).
 *
 *   → {command: 'getUserState', state}
 *   ← {info: 'userState', state, value}
 */
export async function grabCadGetUserState(
  state: string,
  opts: GrabCadOptions = {}
): Promise<string | null> {
  const { target, origin, listenOn } = resolveOpts(opts);

  return new Promise<string | null>((resolve) => {
    const timeoutTimer = setTimeout(() => {
      listenOn.removeEventListener('message', onMessage);
      warnUserStateUnanswered('getUserState', state);
      resolve(null);
    }, USER_STATE_TIMEOUT_MS);

    function onMessage(event: MessageEvent): void {
      const d = event.data;
      if (d?.info !== 'userState' || d?.state !== state) return;
      clearTimeout(timeoutTimer);
      listenOn.removeEventListener('message', onMessage);
      devlog('[GrabCAD userState] got:', state, '=', d.value);
      resolve(d.value === undefined || d.value === null ? null : String(d.value));
    }
    listenOn.addEventListener('message', onMessage);
    devlog('[GrabCAD userState] sending getUserState:', state);
    target.postMessage({ command: 'getUserState', state }, origin);
  });
}

/** Longest we wait for the host to say which it is. A signed-out load waits
 *  on it for its env, so it is short. */
const GET_HOST_TIMEOUT_MS = 1_500;

/**
 * Asks the opener which host it is. Resolves the host's name, or null when
 * there is no opener or no answer in time.
 *
 *   → {command: 'getHost'}
 *   ← {info: 'host', value: 'GrabCAD'}
 */
export async function grabCadGetHost(opts: GrabCadOptions = {}): Promise<string | null> {
  const { target, origin, listenOn } = resolveOpts(opts);
  if (!target) return null;

  return new Promise<string | null>((resolve) => {
    const timeoutTimer = setTimeout(() => {
      listenOn.removeEventListener('message', onMessage);
      resolve(null);
    }, GET_HOST_TIMEOUT_MS);

    function onMessage(event: MessageEvent): void {
      const d = event.data;
      if (d?.info !== 'host') return;
      clearTimeout(timeoutTimer);
      listenOn.removeEventListener('message', onMessage);
      resolve(typeof d.value === 'string' ? d.value : null);
    }
    listenOn.addEventListener('message', onMessage);
    target.postMessage({ command: 'getHost' }, origin);
  });
}

/**
 * OS path of a File dropped inside GrabCAD's Electron host (File.path).
 * Null for synthetic blobs or hosts that don't expose a path.
 */
export function grabCadDroppedFilePath(blob: Blob): string | null {
  const path = (blob as { path?: unknown }).path;
  return typeof path === 'string' && path.length > 0 ? path : null;
}

/**
 * Converts a file whose path is already known — skips the open-file dialog
 * (used for files dropped onto the app, where the OS path comes with the drop).
 * For STL files the unit setting is included (default 'mm'). Resolves with the
 * converted file, or null if the conversion was aborted.
 */
export async function grabCadConvertFile(
  filepath: string,
  opts: GrabCadImportOptions = {}
): Promise<GrabCadImportedFile | null> {
  const { unit, multifile, convertSettings, onProgress, onImportProgress, signal, ...gcOpts } = opts;

  const settings: GrabCadConvertSettings = { ...convertSettings };
  if (filepath.split('.').pop()?.toUpperCase() === 'STL') settings.unit = unit ?? 'mm';

  return grabCadConvertAndOpenFile(filepath, settings, { ...gcOpts, onProgress, signal });
}

/**
 * Converts several files sequentially (openFileData carries no correlation id,
 * so conversions cannot overlap). An aborted conversion stops the remainder of
 * the batch; files converted before the abort are still returned — cancelling
 * means "stop", not "throw away what already worked". Resolves null when
 * nothing was converted.
 *
 * Conversion is the long part of an import: a large STEP file can take minutes,
 * and until the last file lands there is nothing to hand the app. onImportProgress
 * is what lets the caller show that in the meantime.
 */
export async function grabCadConvertFiles(
  filepaths: string[],
  opts: GrabCadImportOptions = {}
): Promise<GrabCadImportedFile[] | null> {
  const results: GrabCadImportedFile[] = [];
  opts.onImportProgress?.(0, filepaths.length);
  for (const filepath of filepaths) {
    const file = await grabCadConvertFile(filepath, opts);
    if (!file) break; // aborted — don't start the remaining conversions
    results.push(file);
    opts.onImportProgress?.(results.length, filepaths.length);
  }
  return results.length ? results : null;
}

/**
 * Full import flow: open-file dialog (multi-select when opts.multifile), then
 * convert each chosen file sequentially. Resolves with the converted files,
 * or null if the user cancelled the dialog or the (first) conversion was
 * aborted.
 */
export async function grabCadImportFile(opts: GrabCadImportOptions = {}): Promise<GrabCadImportedFile[] | null> {
  const filepaths = await grabCadOpenFile(opts);
  if (!filepaths) return null;
  return grabCadConvertFiles(filepaths, opts);
}
